<template>
  <b-container class="my-2 my-sm-5" fluid="lg">
    <h1>{{ $t("myhealth.title") }}</h1>

    <b-row>
      <b-col>
        <b-overlay :show="reportLoading">
          <b-card>
            <b-form @submit.prevent="addActivity">
              <b-row cols="1" cols-md="2" align-v="center">
                <b-col>
                  <FormField
                    tagname="b-form-datepicker"
                    varname="report_dt"
                    label="myhealth.date"
                    v-model="report_dt"
                    :errors="errors"
                    :max="now"
                    :start-weekday="1"
                    hide-header
                    label-help=""
                    :locale="
                      $store.getters.language == 'se'
                        ? 'sv'
                        : $store.getters.language
                    "
                    required
                    v-bind="Object($t('datepicker'))"
                    :date-format-options="{
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                      weekday: undefined,
                    }"
                  />
                </b-col>

                <b-col>
                  <FormField
                    tagname="b-form-select"
                    varname="activity_id"
                    label="activity"
                    v-model="activity_id"
                    :errors="errors"
                  >
                    <b-form-select-option
                      selected
                      disabled
                      value=""
                    ></b-form-select-option>
                    <b-form-select-option
                      v-for="activity in activities"
                      :value="activity.id"
                      :key="activity.id"
                    >
                      {{ activity.transl_name }}
                    </b-form-select-option>
                  </FormField>
                </b-col>

                <b-col>
                  <FormField
                    varname="activity_quantity"
                    :label="unit"
                    :translate-label="false"
                    v-model.number="activity_quantity"
                    :errors="errors"
                    min="1"
                    type="number"
                    required
                  />
                </b-col>

                <b-col>
                  <b-row class="align-items-center" style="row-gap: 1rem">
                    <b-col cols="12" class="cols-xxl-2">
                      <b-button block variant="primary" type="submit">
                        {{ $t("myhealth.submit") }}
                      </b-button>
                    </b-col>
                    <SyncStepsButton
                      wrapper-elem="b-col"
                      @stepsSynced="
                        () => {
                          handleActivitySaved()                          
                        }
                      "
                    />
                  </b-row>
                </b-col>
              </b-row>
              <b-form-invalid-feedback
                :state="error ? false : null"
                class="mb-3"
              >
                {{ error }}
              </b-form-invalid-feedback>
            </b-form>
          </b-card>

          <b-card class="mb-4 mt-4 bg-white" v-if="frequentActivities.length">
            <h6 class="text-terciary font-weight-semibold mb-3">
              {{ $t("myhealth.quick_start") }}
            </h6>

            <div class="d-flex flex-wrap" style="gap: 0.75rem;">
              <div
                v-for="activity in frequentActivities"
                :key="activity.id"
                class="quick-start-button p-2 rounded-lg"
                @click="openQuickReport(activity)"
              >
                <div class="d-flex align-items-center">
                  <div class="quick-start-icon mr-2">
                    <b-img
                      v-if="activity.activity.icon_file_name"
                      :src="activity.activity.icon_file_path + activity.activity.icon_file_name"
                      fluid
                      style="max-height: 1rem;"
                    />
                  </div>
                  <span class="quick-start-label">
                    {{ activity.activity.translations.name }} +
                  </span>
                </div>
              </div>
            </div>
          </b-card>
        </b-overlay>
        <b-tabs
          class="my-3"
          :justified="['xs', 'sm', 'md'].includes($store.state.breakpoint)"
          :nav-class="
            ['xs', 'sm', 'md'].includes($store.state.breakpoint) ? 'w-100' : ''
          "
        >
          <b-tab
            title-link-class="px-1 px-sm-4.5 h-100 d-flex align-items-center justify-content-center"
            :title="$t('myhealth.daily_summary')"
            class="py-2 py-sm-4 px-1 px-sm-4"
            active
          >
            <b-overlay :show="graphLoading">
              <small>{{ $t("myhealth.points_overview") }}</small>
              <br />
              <small class="text-muted">{{ $t("myhealth.pantoshow") }}</small>

              <PointChart
                :data="chartData"
                :unit="`${$t('points')}`"
                :goal="100"
                @scrollEnd="showOlder()"
              />
              <!-- Bar chart Legend -->
              <div class="d-flex flex-wrap align-items-center mt-3">
                <div
                  v-for="type in [
                    { name: 'activitypoints', color: 'var(--primary)' },
                    { name: 'missionpoints', color: 'var(--success)' },
                    { name: 'quizpoints', color: 'var(--accent)' },
                  ]"
                  :key="type.name"
                  class="d-flex align-items-center mr-4 mb-2"
                  style="font-size: 0.9rem;"
                >
                  <span
                    class="d-inline-block mr-2"
                    style="width: 0.75rem; height: 0.75rem; border-radius: 50%;"
                    :style="{ background: type.color }"
                  />
                  <span>{{ $t(type.name) }}</span>
                </div>
              </div>

            </b-overlay>
          </b-tab>
          <!-- Activity tab -->
          <b-tab
            title-link-class="px-1 px-sm-4.5 h-100 d-flex align-items-center justify-content-center"
            :title="$t('myhealth.my_activities')"
            class="overflow-hidden"
            lazy
          >
            <b-overlay :show="myActivitiesLoading">
              <b-table-simple
                striped
                small
                hover
                responsive
                class="mb-0 px-md-3"
              >
                <colgroup>
                  <col v-for="(_, i) in Array(columns.length)" :key="i" />
                  <col />
                </colgroup>

                <b-thead>
                  <b-th v-for="column in columns" :key="column">
                    <span class="d-inline-flex align-items-center" style="gap: 0.35rem;">
                      <span>{{ $t(column) }}</span>
                      <span
                        v-if="column === 'points' && maxDailyActivityPoints != null"
                        class="d-inline-flex align-items-center"
                        style="cursor: pointer;"
                        tabindex="0"
                        v-b-tooltip.hover
                        :title="$t('myhealth.points_cap_info', { cap: maxDailyActivityPoints })"
                      >
                        <b-icon-info-circle />
                      </span>
                    </span>
                  </b-th>
                  <b-th style="width: auto"></b-th>
                </b-thead>

                <b-tbody>                  
                  <b-tr
                    v-for="activity in myActivities"
                    :key="activity.source_hashkey"
                  >
                    <b-td>{{ activity.report_dt }}</b-td>
                    <b-td style="word-wrap: anywhere;">
                    <b-img
                      v-if="activity.icon_file_name"
                      :src="`${activity.icon_file_path}${activity.icon_file_name}`"
                      fluid
                      class="mr-2"
                      style="max-height: 1.2em; width: auto; vertical-align: middle;"
                      :alt="activity.transl_name"
                      :title="activity.transl_name"
                    />                      
                    <b-img
                      :src="activity.data_provider_logo_file_name ? `${activity.data_provider_logo_file_path}/${activity.data_provider_logo_file_name}` : ''"
                      fluid
                      class="mr-2"
                      style="max-height: 1.2em; width: auto; vertical-align: middle;"
                      v-if="activity.data_provider && activity.data_provider_logo_file_path"
                      :title="activity.data_provider"
                    />
                      {{ activity.transl_name }}
                    </b-td>
                    <b-td>
                      {{
                        activity.steps
                          ? separateThousand(activity.activity_quantity)
                          : null
                      }}
                    </b-td>
                    <b-td>
                      {{
                        activity.steps
                          ? null
                          : separateThousand(activity.activity_quantity)
                      }}
                    </b-td>
                    <b-td>
                      <div class="points-display">
                        <span class="points-value">
                          {{ activity.points_awarded }}
                        </span>

                        <span
                          v-if="activity.points_fully_capped"
                          class="points-cap-note"
                          v-b-tooltip.hover
                          :title="$t('myhealth.points_cap_tooltip_full')"
                        >
                          ({{ $t('myhealth.cap_label') }})
                        </span>

                        <span
                          v-else-if="activity.points_capped"
                          class="points-cap-note"
                          v-b-tooltip.hover
                          :title="$t('myhealth.points_cap_tooltip_partial')"
                        >
                          ({{ $t('myhealth.cap_label') }})
                        </span>
                      </div>
                    </b-td>

                    <b-td>
                      <b-icon-trash
                        :title="$t('myhealth.delete_activity')"
                        font-scale="1.1"
                        class="text-danger cursor-pointer"
                        @click="deleteActivity(activity.source_hashkey)"
                      />
                    </b-td>
                  </b-tr>

                  <b-tr v-if="next_page_url">
                    <b-td :colspan="columns.length + 1">
                      <b-button variant="primary" block @click="showOlder">
                        {{
                          `... ${$t(
                            "myhealth.earlier_activities"
                          )} ...`.toLocaleLowerCase()
                        }}
                      </b-button>
                    </b-td>
                  </b-tr>
                </b-tbody>

                <b-tfoot>
                  <b-td
                    variant="primary"
                    class="font-weight-bold"
                    colspan="2"
                  >
                    {{ $t("total") }}:
                  </b-td>
                  <b-td variant="primary">
                    {{
                      `${separateThousand(totalSteps)} ${$t(
                        "steps"
                      )}`.toLocaleLowerCase()
                    }}
                  </b-td>
                  <b-td variant="primary">
                    {{
                      `${separateThousand(totalMinutes)} ${$t(
                        "minutes"
                      )}`.toLocaleLowerCase()
                    }}
                  </b-td>
                  <b-td variant="primary">
                    {{
                      `${separateThousand(myTotalPoints)} ${$t(
                        "points"
                      )}`.toLocaleLowerCase()
                    }}
                  </b-td>
                  <b-td variant="primary"></b-td>
                </b-tfoot>
              </b-table-simple>
            </b-overlay>
          </b-tab>
          <!-- Guided Workouts Tab -->
          <b-tab
            title-link-class="px-1 px-sm-4.5 h-100 d-flex align-items-center justify-content-center"
            :title="$t('myhealth.guided_workouts')"
            class="py-2 py-sm-4 px-1 px-sm-4"
            lazy
            @activated="fetchFilteredWorkouts"
          >
            <b-overlay :show="workoutsLoading" rounded opacity="0.8">
              <!-- Filters Section -->
            <div class="filter-container">
              <!-- Category Filter -->
              <b-form-group class="mb-3">
                <b-form-select
                  v-model="selectedCategory"
                  @change="fetchFilteredWorkouts"
                  size="md"
                  class="custom-select"
                >
                  <!-- Default Placeholder (acts as reset) -->
                  <b-form-select-option value="">
                    {{ $t('myhealth.select_category') }}
                  </b-form-select-option>

                  <!-- Dynamic Options -->
                  <b-form-select-option
                    v-for="category in workoutCategories"
                    :value="category.original"
                    :key="category.original"
                  >
                    {{ category.translated }}
                  </b-form-select-option>
                </b-form-select>
              </b-form-group>

              <!-- Difficulty Filter -->
              <b-form-group>
                <b-form-select
                  v-model="selectedDifficulty"
                  @change="fetchFilteredWorkouts"
                  size="md"
                  class="custom-select"
                >
                  <!-- Default Placeholder (acts as reset) -->
                  <b-form-select-option value="">
                    {{ $t('myhealth.select_difficulty') }}
                  </b-form-select-option>

                  <!-- Dynamic Options -->
                  <b-form-select-option
                    v-for="difficulty in difficulties"
                    :value="difficulty"
                    :key="difficulty"
                  >
                    {{ $t(`myhealth.difficulties.${difficulty}`) }}
                  </b-form-select-option>
                </b-form-select>
              </b-form-group>

              <!-- Optional "Clear Filters" Button -->
              <b-button
                v-if="selectedCategory || selectedDifficulty"
                variant="outline-primary"
                size="sm"
                class="mt-0 mb-4"
                @click="clearFilters"
              >
                {{ $t('myhealth.clear_filters') }}
              </b-button>
            </div>
            
                <!-- Slimmed Workout List -->
              <b-list-group>
                <b-list-group-item
                  v-for="workout in guidedWorkouts"
                  :key="workout.id"
                  class="d-flex align-items-center justify-content-between py-3"
                >
                  <!-- Thumbnail and Info -->
                  <div class="d-flex align-items-center">
                    <b-img
                      :src="workout.thumb_media_file ? workout.thumb_media_file.file_path + workout.thumb_media_file.file_name : ''"
                      alt="Workout Thumbnail"
                      width="100%"
                      height="70"
                      
                      rounded
                      class="mr-3"
                    ></b-img>
                    <div>
                        <h6 class="mb-1 font-weight-bold">
                          {{ workout.translations && workout.translations.length > 0 ? workout.translations[0].name : '' }}
                        </h6>
                      <small class="text-muted">
                        {{ workout.translations && workout.translations.length > 0 ? workout.translations[0].category : '' }} · 
                        {{ workout.translations && workout.translations.length > 0 ? workout.translations[0].difficulty : '' }} · 
                        {{ workout.duration }} min
                      </small>
                    </div>
                  </div>
                  <!-- Points and Action -->
                  <div class="d-flex align-items-center">
                    <span class="text-primary font-weight-bold mr-4 no-wrap">{{ workout.total_points || 0 }} {{ $t('points') }}</span>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="openWorkoutModal(workout.id)"
                      class="d-flex align-items-center justify-content-center"
                    >
                      <b-icon-playFill class="icon mr-1" />
                      {{ $t("myhealth.join_workout") }}
                    </b-button>
                  </div>
                </b-list-group-item>

                <!-- No Workouts Found -->
                <div v-if="!workoutsLoading && guidedWorkouts.length === 0" class="text-center text-muted py-5">
                  {{ $t("myhealth.no_workouts_found") }}
                </div>
              </b-list-group>
            </b-overlay>
          </b-tab>
        </b-tabs>
      </b-col>

      <b-col class="mt-3 mt-sm-0" cols="12" xl="auto">
       <div style="max-width: 320px; width: 100%;">        
        <b-overlay :show="statisticsLoading">
          <b-card no-body tag="aside" class="pb-4 pt-4">
            <!--
            <template #header>
              <h2 class="mb-0">{{ $t("statistics") }}</h2>
            </template>-->
            <div class="position-relative mx-auto">
              <CircleProgress :value="myPoints" :max="totalPoints" />

              <div
                class="position-absolute d-flex flex-column justify-content-center align-items-center rounded-circle p-3 text-center font-weight-semibold"
                style="top: 50%; left: 50%; transform: translate(-50%, -50%);"
                :style="{
                  // 100% minus stroke minus margin
                  width: 'calc(100% - 20px - 25px)',
                  height: 'calc(100% - 20px - 25px)',
                  backgroundColor: '#f6f4f4',
                  fontSize: '1.25rem',
                }"
              >
                <div>{{ $t("points") }}</div>
                <div>
                  <span :class="myPoints < totalPoints && 'text-terciary'">
                    {{ myPoints }}
                  </span>
                  /
                  <span>{{ totalPoints }}</span>
                </div>
              </div>
            </div>
            <b-alert
              v-if="!insideCampaignPeriod"
              variant="info"
              show
              class="mt-3 pb-0 text-highlight text-center font-italic border-0 bg-transparent"
            >
              <b-icon-info-circle class="mr-1" /> 
              {{ $t("myhealth.campaign_info") }}
            </b-alert>
            <div class="text-center mt-3" v-if="insideCampaignPeriod">
              <b-badge
                :class="['p-2 font-weight-semibold', onTrack ? 'on-track' : 'off-track']"
                v-b-tooltip.hover
                :title="$t('myhealth.points_needed_tooltip')"
              >
                <component
                  :is="onTrack ? 'b-icon-check-circle-fill' : 'b-icon-exclamation-triangle-fill'"
                  class="mr-1"
                />
                  {{ neededPointsPerDay }} {{ $t('myhealth.points_per_day') }}.
                  {{ onTrack ? $t('myhealth.on_track') : $t('myhealth.falling_behind') }}
              </b-badge>
            </div>        
            <!--
            <b-list-group flush>
              <b-list-group-item>
                <div
                  class="mb-3 d-flex justify-content-between align-items-baseline text-terciary font-weight-semibold"
                >
                  <div class="mr-3">{{ $t("myhealth.average_steps") }}:</div>
                  <div>{{ stepAvg }}</div>
                </div>
                <b-progress :value="stepAvg" :max="10000" />
              </b-list-group-item>
              <b-list-group-item>
                <div
                  class="mb-3 d-flex justify-content-between align-items-baseline text-terciary font-weight-semibold"
                >
                  <div class="mr-3">{{ $t("myhealth.highest_steps") }}:</div>
                  <div>{{ pb }}</div>
                </div>
                <b-progress :value="pb" :max="10000" />
              </b-list-group-item>
              <b-list-group-item>
                <div
                  class="mb-3 d-flex justify-content-between align-items-baseline text-terciary font-weight-semibold"
                >
                  <div class="mr-3 baseline text-terciary">{{ $t("myhealth.points_average") }}:</div>
                  <div>{{ pointsAvg }}</div>
                </div>
                <b-progress :value="pointsAvg" :max="100" />
              </b-list-group-item>

                    
            </b-list-group>-->         
          </b-card>
        </b-overlay>
        <!-- Today's Progress Card -->
        <b-card no-body tag="aside" class="mt-3">
          <b-card-body class="p-3 bg-primary text-light rounded">
            <div class="d-flex align-items-center mb-3">
              <b-icon-trophy font-scale="1.2" variant="light" class="mr-2" />
              <h5 class="mb-0 font-weight-semibold">{{ $t("myhealth.todays_progress") }}</h5>
            </div>
            <div class="h4 font-weight-bold mb-2">{{ todayPoints }}/{{ targetPoints }}</div>
            <div class="progress mb-2" style="height: 6px; background-color: rgba(255, 255, 255, 0.2);">
              <div
                class="progress-bar bg-light"
                role="progressbar"
                :style="{ width: progressPercent + '%' }"
                :aria-valuenow="todayPoints"
                aria-valuemin="0"
                :aria-valuemax="targetPoints"
              ></div>
            </div>
            <div class="small text-light-90">{{ goalText }}</div>
          </b-card-body>
        </b-card>

        <!-- Streak KPI Card -->
        <b-card no-body tag="aside" class="mt-3 streak-card">
          <b-card-body class="p-3">
            <div class="d-flex justify-content-between align-items-start">
              <!-- Left side: Icon + Current Streak -->
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-light d-flex align-items-center justify-content-center mr-3">
                  <b-icon-lightning font-scale="1.2" class="text-light" />
                </div>
                <div>
                  <div class="small text-terciary">{{ $t("myhealth.current_streak") }}</div>
                  <div class="font-weight-bold h5 text-primary">
                    {{ streakData.current }} {{ $t("myhealth.days") }}
                  </div>
                </div>
              </div>
              <!-- Right side: Best Streak -->
              <div class="text-right">
                <div class="small text-terciary">{{ $t("myhealth.best_streak") }}</div>
                <div class="font-weight-bold text-terciary">
                  {{ streakData.longest }} {{ $t("myhealth.days") }}
                </div>
              </div>
            </div>
            <div class="mt-3 small text-terciary">
              {{ streakFooterText }}
            </div>
          </b-card-body>
        </b-card>     
      </div>        
      </b-col>
    </b-row>    

    <!-- Workout Modal -->
    <WorkoutModal
      :workoutId="selectedWorkoutId"
      v-if="showWorkoutModal"
      @close="closeWorkoutModal"
      @activity-saved="handleActivitySaved"
    />

    <QuickReportModal
      v-if="showQuickReportModal"
      :data="quickReportData"
      @close="showQuickReportModal = false"
      @save="submitQuickActivity"
    />      
  </b-container>  
</template>

<script>
import { BIconTrash, BIconPlayFill, BIconLightning, BIconTrophy, BIconCheckCircleFill, BIconExclamationTriangleFill, BIconInfoCircle, BIconLockFill } from "bootstrap-vue"
import axios from "axios"
import FormField from "@/components/FormField.vue"
import PointChart from "@/components/PointChart.vue"
import CircleProgress from "@/components/CircleProgress.vue"
import SyncStepsButton from "@/components/SyncStepsButton.vue"
import WorkoutModal from "@/components/WorkoutModal.vue";
import QuickReportModal from "@/components/QuickReportModal.vue";

export default {
  components: {
    BIconTrash,
    BIconPlayFill,
    BIconLightning,
    BIconTrophy,
    BIconCheckCircleFill,
    BIconExclamationTriangleFill,
    BIconInfoCircle,
    BIconLockFill,
    FormField,
    PointChart,
    CircleProgress,
    SyncStepsButton,
    WorkoutModal,
    QuickReportModal
  },

  data() {
    return {
      now: "",
      reportLoading: false,
      myActivitiesLoading: false,
      statisticsLoading: false,
      graphLoading: false,
      chartSymmaryData: [],
      error: "",
      errors: {},
      report_dt: "",
      activity_id: 2,
      /** @type {Array<any>} */
      activities: [],
      activity_quantity: null,
      columns: ["date", "activity", "steps", "minutes", "points"],
      /** @type {Array<any>} */
      myActivities: [],
      next_page_url: null,
      syncStepsLoading: false,
      todayPoints: 0,
      targetPoints: 0, 
      stepAvg: null,
      pb: null,
      calculatedStartDt: null,
      /** @type {Number | undefined} */
      totalPoints: null,
      /** @type {Number | undefined} */
      myPoints: null,
      pointsAvg: null,
      campaignStartDt: null,
      campaignEndDt: null,    
      neededPointsPerDay: null,
      onTrack: null,
      workoutCategories: [], // Categories fetched from the API
      difficulties: ["Beginner", "Intermediate", "Advanced"], // Static English difficulties
      selectedCategory: "", // Selected category filter
      selectedDifficulty: "", // Selected difficulty filter
      guidedWorkouts: [], // Workouts to display
      workoutsLoading: false,
      selectedWorkoutId: null,
      showWorkoutModal: false,     
      frequentActivities: [],
      showQuickReportModal: false,
      quickReportData: null,  
      streakData:[],
      maxDailyActivityPoints: null,
      insideCampaignPeriod: null,
    }
  },
  computed: {
    streakFooterText() {
    return `${this.$t('myhealth.streak_footer_prefix')} ${this.streakData.requiredPointsPerDay} ${this.$t('myhealth.streak_footer')}`
  },    
    progressPercent() {
      return Math.min((this.todayPoints / this.targetPoints) * 100, 100)
    },
    goalText() { 
      return this.targetPoints - this.todayPoints > 0
        ? `${this.targetPoints - this.todayPoints} ${this.$t('myhealth.points_remaining')}`
        : this.$t('myhealth.goal_achieved')
    },   
    /**
     * Current activity
     * @return {Object} */
    currentActivity() {
      return this.activities.find(e => e.id == this.activity_id)
    },
    /** @return {String} */
    unit() {
      if (this.currentActivity) {
        const unitname = this.currentActivity.activity_unit_name.toLowerCase()
        return `${this.$t("amount")} ${this.$t(unitname).toLowerCase()}`
      }
      return this.$t("quantity")
    },
    /** @return {Number} */
    totalSteps() {
      const steps = this.myActivities.map(e => {
        if (e.steps) return e.activity_quantity
        return 0
      })
      if (steps.length) {
        return steps.reduce((acc, val) => acc + val)
      }
      return 0
    },
    /** @return {Number} */
    totalMinutes() {
      const minutes = this.myActivities.map(e => {
        if (!e.steps) return e.activity_quantity
        return 0
      })
      if (minutes.length) {
        return minutes.reduce((acc, val) => acc + val)
      }
      return 0
    },
    /** @return {Number} */
    myTotalPoints() {
      if (this.myActivities.length) {
        return this.myActivities.reduce(
          (acc, val) => acc + Number(val.points_awarded ?? val.points ?? 0),
          0
        )
      }
      return 0
    },
    /** @return {Array<any>} */
    chartData() {
      if (!this.chartSymmaryData.length) {
        return []
      }
      // Filter old dates and sort ascending
      const sortedActivities = this.chartSymmaryData
        .slice()
        .sort((a, b) => new Date(a.report_dt) - new Date(b.report_dt)) // Sort by date
      // Get date one year from last date
      let oneYear = new Date(
        sortedActivities[sortedActivities.length - 1].report_dt
      )
      oneYear.setFullYear(oneYear.getFullYear() - 1)
      // Filter dates that are more than a year ago
      const filteredActivities = sortedActivities.filter(
        e => new Date(e.report_dt) > oneYear
      )
      let summed = {}
      // Create object to prevent duplicates
      filteredActivities.map(e => {
        // Populate object for each date
        if (e.report_dt in summed) {
          summed[e.report_dt].points_activity += parseInt(e.points_activity, 10)
          summed[e.report_dt].points_bonus += parseInt(e.points_bonus, 10)
          summed[e.report_dt].points_quiz += parseInt(e.points_quiz, 10)
        } else {
          summed[e.report_dt] = {
            date: new Date(e.report_dt),
            // name: e.source_hashkey,
            points_activity: parseInt(e.points_activity, 10) || 0,
            points_bonus: parseInt(e.points_bonus, 10) || 0,
            points_quiz: parseInt(e.points_quiz, 10) || 0,
          }
        }
      })
      // Change object back to array
      let pointdata = Object.values(summed)
      // Interpolate missing dates and add zero-value for those
      if (pointdata.length >= 2) {
        const one_day = 1000 * 60 * 60 * 24
        for (let i = 0; i < pointdata.length; i++) {
          const date = pointdata[i].date
          const nextDate = pointdata[i + 1]?.date || new Date()
          if (nextDate) {
            if (nextDate - date > one_day) {
              let dayAfter = new Date(pointdata[i].date.getTime())
              dayAfter.setDate(dayAfter.getDate() + 1)
              pointdata.splice(i + 1, 0, {
                date: dayAfter,
                // name: "blank" + i,
                points_activity: 0,
                points_bonus: 0,
                points_quiz: 0,
              })
            }
          }
        }
      }
      return pointdata
    },
  },

  created() {
    let d = new Date()
    let year = d.getFullYear().toString()
    let month = (d.getMonth() + 1).toString()
    let day = d.getDate().toString()
    this.now = `${year}-${month.length < 2 ? "0" + month : month}-${
      day.length < 2 ? "0" + day : day
    }`
    // Date selector defaults to today
    this.report_dt = this.now

    this.getActivities()
    this.getMyActivities()
    this.getChartSummary()
    this.getStatistics()
    this.getWorkouts()
    this.fetchWorkoutCategories()
    this.fetchFilteredWorkouts()
    this.getStreakData()
    this.getFrequentActivities()
  },
  methods: {  
    async getFrequentActivities() {
      try {
        const response = await axios.get("/useractivity/popularactivities")
        this.frequentActivities = response.data.data
      } catch (error) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: this.$t("error_loading_quick_activities"),
        })
      }
    },
    openQuickReport(activity) {
      this.quickReportData = {
        id: activity.activity.id,
        name: activity.activity.translations.name,
        defaultQuantity: activity.recent_activity_quantity,
        points: activity.activity.points,
        stepsPerMinute: activity.activity.no_of_steps_per_minute,
      }
      this.showQuickReportModal = true
    }, 
    submitQuickActivity({ activity_id, quantity }) {      
      this.activity_id = activity_id
      this.activity_quantity = quantity
      this.report_dt = this.now
      this.addActivity() 
      this.showQuickReportModal = false
    },       
    async getStreakData() {
      try {
        const response = await axios.get("/useractivity/streaks")
        const {
          current_streak,
          longest_streak,
          required_points_per_day,
        } = response.data.data

        this.streakData = {
          current: current_streak,
          longest: longest_streak,
          requiredPointsPerDay: required_points_per_day,
        }
      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response?.data?.error_message ?? `${name}: ${message}`,
        })
      }
    },

    separateThousand(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    async getActivities() {      
      this.reportLoading = true

      try {
        const response = await axios.get("/activitylist")
        this.activities = response.data?.data ?? []
      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response.data.error_message ?? `${name}: ${message}`,
        })
      } finally {
        this.reportLoading = false
      }
    },
    async getMyActivities(next_url) {      
      this.myActivitiesLoading = true
      let url = next_url ? new URL(next_url) : null

      try {
        // url?.search extracts query params and applies it to current url
        const response = await axios.get(
          "/useractivity/my" + (url?.search || "")
        )
        const { data, links } = response.data
        if (url?.search) {
          // If fetching next page --> append to current activities
          this.myActivities = this.myActivities.concat(data)
        } else {
          this.myActivities = data
        }

        // Normalize cap/points fields once so template stays clean
        this.myActivities.forEach(a => {
          const raw = Math.round(Number(a.points_before_cap ?? a.points ?? 0))
          const awarded = Math.round(Number(a.points_after_cap ?? a.points ?? 0))

          a.points_raw = raw
          a.points_awarded = awarded
          a.points_capped = awarded > 0 && awarded < raw
          a.points_fully_capped = awarded === 0 && raw > 0
        })
        this.next_page_url = links.next_page_url

      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response.data.error_message ?? `${name}: ${message}`,
        })
      } finally {
        this.myActivitiesLoading = false
        // Sort descending
        this.myActivities.sort(
          (a, b) => new Date(b.report_dt) - new Date(a.report_dt)
        )
      }
    },
    async getChartSummary() {
      this.graphLoading = true
      try {
        const response = await axios.get("/useractivity/summary")
        const { data } = response.data
        this.chartSymmaryData = data

        // Use first item as today’s data
        if (data.length > 0) {
          const todayData = data[0]

          this.targetPoints = todayData.points_daily_goal
          this.todayPoints =
            (todayData.points_activity || 0) +
            (todayData.points_bonus || 0) +
            (todayData.points_quiz || 0)
        }
      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response?.data?.error_message ?? `${name}: ${message}`,
        })
      } finally {
        this.graphLoading = false
      }
    },
    async getStatistics() {
      this.statisticsLoading = true

      try {
        const response = await axios.get("/useractivity/myhealthstat")
        const {
          stepAvg,
          pb,
          calculatedStartDt,
          totalPoints,
          myPoints,
          pointsAvg,
          neededPointsPerDay, 
          onTrack,
          campaignStartDt,
          campaignEndDt,
          insideCampaignPeriod,
          maxDailyActivityPoints
        } = response.data.data

        Object.assign(this, {
          stepAvg,
          pb,
          calculatedStartDt,
          totalPoints: parseFloat(totalPoints),
          myPoints: parseFloat(myPoints),
          pointsAvg,
          neededPointsPerDay,
          onTrack,          
          campaignStartDt,
          campaignEndDt,
          insideCampaignPeriod,
          maxDailyActivityPoints
        })
      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response?.data?.error_message ?? `${name}: ${message}`,
        })
      } finally {
        this.statisticsLoading = false
      }
    },


    async showOlder() {
      if (this.next_page_url) {
        this.getMyActivities(this.next_page_url)
      }
    },

    async addActivity() {
      this.reportLoading = true
      this.error = ""
      this.errors = {}

      try {
        // Get values to be posted
        const { report_dt, activity_id, activity_quantity } = this
        const response = await axios.post("/useractivity", {
          report_dt,
          activity_id,
          activity_quantity,
        })
        // Show a card with latest points
/*        let latestPoints = response.data?.points ?? 0
        // Show global alert
        if (latestPoints)
          this.$store.dispatch("addToastMessage", {
            type: "success",
            text: `${this.$t(
              "myhealth.report-success"
            )} ${latestPoints} ${this.$t("points").toLocaleLowerCase()}`,
            dismissSecs: 10,
          })
*/       
          // Show a card with latest points
          const after = Math.round(Number(response.data?.points_after_cap ?? response.data?.points ?? 0))
          const before = Math.round(Number(response.data?.points_before_cap ?? after))

          let text = ""

          if (after > 0 && after === before) {
            text = this.$t("myhealth.activity_saved_points", { points: after })
          } else if (after > 0 && after < before) {
            text = this.$t("myhealth.activity_saved_points_cap_reached", { points: after })
          } else if (after === 0 && before > 0) {
            text = this.$t("myhealth.activity_saved_cap_already_reached")
          } else {
            // fallback (use a generic key if you have one)
            text = this.$t("myhealth.activity_saved") // or keep hardcoded if you don't have this key
          }
          this.$store.dispatch("addToastMessage", {
            type: "success",
            text,
            dismissSecs: 10,
          })
        // Clear form
        this.activity_quantity = null
      } catch ({ name, message, response }) {
        if (response?.data) {
          const { errors, message } = response.data
          this.error = message
          this.errors = errors
        } else {
          this.error = `${name}: ${message}`
          this.$store.dispatch("addToastMessage", {
            type: "danger",
            text: response.data.error_message ?? `${name}: ${message}`,
          })
        }
      } finally {
        this.reportLoading = false
        this.handleActivitySaved()
      }
    },

    async deleteActivity(source_hashkey) {
      // Show confirmation dialog
      const confirm = await this.$bvModal.msgBoxConfirm(
        this.$t("myhealth.delete_activity") + "?",
        {
          okTitle: this.$t("delete"),
          okVariant: "primary",
          cancelTitle: this.$t("cancel"),
          cancelVariant: "outline-primary"
        }
      )
      if (!confirm) return

      this.myActivitiesLoading = true

      try {
        await axios.delete("/useractivity", {
          data: { source_hashkey },
        })
      } catch ({ name, message, response }) {
        this.$store.dispatch("addToastMessage", {
          type: "primary",
          text: response.data.error_message ?? `${name}: ${message}`,
        })
      } finally {
        this.myActivitiesLoading = false
        this.getMyActivities()
        this.getChartSummary()
        this.getStatistics()
      }
    },
    async getWorkouts() {      
      // If you prefer always refreshing, remove this "already loaded" check
      if (this.guidedWorkouts.length > 0) return

      this.workoutsLoading = true
      try {
        // Adjust as needed if your API path/response structure differs
        const response = await axios.get("/workouts")
        // response.data will be { data: [ {id, name, ...}, ... ] }
        this.guidedWorkouts = response.data?.data || []
      } catch (error) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: error.response?.data?.error_message || error.message,
        })
      } finally {
        this.workoutsLoading = false
      }
    },
    joinWorkout(workout) {
      // Example: a simple message or real logic
      this.$store.dispatch("addToastMessage", {
        type: "success",
        text: `Joining workout: ${workout.name}`,
      })
      // Optionally, open a modal or navigate to a detail page
    },
    async fetchWorkoutCategories() {
      try {
        const response = await axios.get("/workout/categories");
        this.workoutCategories = response.data?.data || [];
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    },
    async fetchFilteredWorkouts() {
      this.workoutsLoading = true;

      try {
        const params = {};
        if (this.selectedCategory) params.category = this.selectedCategory;
        if (this.selectedDifficulty) params.difficulty = this.selectedDifficulty;

        const response = await axios.get("/workouts", { params });
        this.guidedWorkouts = response.data?.data || [];
      } catch (error) {
        console.error("Failed to fetch workouts:", error);
      } finally {
        this.workoutsLoading = false;
      }
    },
    clearFilters() {
      this.selectedCategory = "";
      this.selectedDifficulty = "";
      this.fetchFilteredWorkouts();
    },    
    openWorkoutModal(workoutId) {
      this.selectedWorkoutId = workoutId;
      this.showWorkoutModal = true;
    },
    closeWorkoutModal() {
      this.selectedWorkoutId = null;
      this.showWorkoutModal = false;
    },   
    handleActivitySaved() {
      // Refresh the activity list, chart, and statistics
      this.getMyActivities();
      this.getChartSummary();
      this.getStatistics();      
      this.getStreakData();
      this.getFrequentActivities();
    },     
  },
}
</script>

<style scoped>
@media (min-width: 1400px) {
  .cols-xxl-2:not(:only-child) {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

.no-wrap {
  white-space: nowrap;
  flex-shrink: 0;
}

.streak-card {
  background-color: rgba(230, 51, 117, 0.1); 
  border: none;
}

.icon-circle {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
}


.on-track {
  color: #00C782; /* Green for On Track */
  background-color: rgba(0, 199, 130, 0.15);
}

.off-track {
  color: #E74C3C; /* Red for Falling Behind */
  background-color: rgba(231, 76, 60, 0.15); /* Light red background */
}

.quick-start-button {
  background-color: rgba(230, 51, 117, 0.05); 
  flex: 1 1 48%;
  max-width: 48%;
  cursor: pointer;
}

.quick-start-icon {
  background-color: rgba(230, 51, 117, 0.1); 
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-start-label {
  color: #E63375; 
  font-size: 0.875rem;
  font-weight: 700;
}


.points-display {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.points-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.points-cap-note {
  font-size: 0.8em;
  color: #6b7280;
  font-weight: 500;
  cursor: help;
  white-space: nowrap;
}

.points-cap-note:hover {
  color: #4b5563;
}

</style>
